import { Icon, IconName } from '@clarion/ClarionIcon';
import { ReactNode } from 'react';
import {
  TextField as RACTextField,
  TextFieldProps as RACTextFieldProps,
  composeRenderProps,
} from 'react-aria-components';
import { tv } from 'tailwind-variants';
import {
  ClarionDescription,
  ClarionFieldError,
  ClarionFieldGroup,
  ClarionInput,
  ClarionLabel,
} from './ClarionField';

export interface ClarionTextFieldProps extends Omit<RACTextFieldProps, 'children'> {
  label?: string;
  description?: string;
  errorMessage?: string | ((validation: any) => string);
  placeholder?: string;

  icon?: IconName;
  endElement?: ReactNode;
  size?: 'small' | 'medium' | 'large';
  fill?: boolean;
}

const textFieldStyles = tv({
  base: 'flex flex-col gap-1',
  variants: {
    fill: {
      true: 'w-full',
    },
  },
});

export function ClarionTextField({
  label,
  description,
  errorMessage,
  placeholder,
  icon,
  endElement,
  size = 'medium',
  fill,
  className,
  ...props
}: ClarionTextFieldProps) {
  return (
    <RACTextField
      {...props}
      className={composeRenderProps(className, (className, renderProps) =>
        textFieldStyles({ ...renderProps, fill, className }),
      )}
    >
      {label && <ClarionLabel>{label}</ClarionLabel>}
      <ClarionFieldGroup isInvalid={props.isInvalid} size={size}>
        {icon && <Icon icon={icon} className="ml-2.5 text-neutral-500 dark:text-neutral-400" />}
        <ClarionInput placeholder={placeholder} className={icon ? 'pl-2' : undefined} />
        {endElement && <div className="mr-1 flex items-center">{endElement}</div>}
      </ClarionFieldGroup>
      {description && <ClarionDescription>{description}</ClarionDescription>}
      <ClarionFieldError>{errorMessage}</ClarionFieldError>
    </RACTextField>
  );
}
